import { FunctionComponent, useCallback, useEffect, useMemo } from "react";
import { SidePanelDS, Spacer, sortByDateTime, triggerToast } from "@qivia/ui";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../../redux/hooks";
import { PublicTransactionsPropsType } from "../transactionsAPI";
import {
  selectAuthorizationsList,
  selectSendSmsReplaceInvalidStatus,
  selectSendSmsStatus,
  selectTransactionsList,
} from "../transactionsSlice";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import "../../../../i18n/i18n";
import { fakeTransactionsDisplayedForDemo } from "../lib";
import { TransactionsDetails } from "./transactionDetails";
import { SidePanelHeader } from "./sidePanelHeader";
import { SidePanelSectionBlock } from "./sectionBlock";
import { useTranslation } from "react-i18next";
import { LinkedTransactionBlock } from "./cardsSidePanel/linkedTransactionBlock";

type TransactionsSidePanelProps = {
  canRedirectToRule?: boolean;
  canCorrectMerchantCategory?: boolean;
  sendSmsReplaceInvalidAction: (props: {
    driverPhone: string;
    driverName: string;
    transactionUuid: string;
  }) => void;
  getSupportingDocumentAction: (supportingDocumentUuid: string) => void;
  getLastReminderDateAction: (transactionUuid: string) => void;
  sendSmsAction: (props: {
    driverPhone: string;
    driverName: string;
    transactionUuid: string;
  }) => void;
};

export const TransactionsSidePanel: FunctionComponent<
  TransactionsSidePanelProps
> = (props: TransactionsSidePanelProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const firstPath = location.pathname.split("/")[1]; // home or departments

  const transactionsList: PublicTransactionsPropsType[] | null = useAppSelector(
    selectTransactionsList,
  );
  const authorizationsRefusedList: PublicTransactionsPropsType[] | null =
    useAppSelector(selectAuthorizationsList);

  const isDemoEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] === "demo";
  }, []);

  const allTransactionsDisplayed: PublicTransactionsPropsType[] =
    useMemo(() => {
      if (!transactionsList || !authorizationsRefusedList) return [];
      const list = [...transactionsList, ...authorizationsRefusedList];
      const listDisplayed = isDemoEnvironment()
        ? [...list, ...fakeTransactionsDisplayedForDemo]
        : list;
      return sortByDateTime(listDisplayed, "desc");
    }, [authorizationsRefusedList, isDemoEnvironment, transactionsList]);

  const transaction = allTransactionsDisplayed.find(
    (t) => t.uuid === params.id,
  );

  const sendSmsStatus = useAppSelector(selectSendSmsStatus);
  const sendSmsReplaceInvalidStatus = useAppSelector(
    selectSendSmsReplaceInvalidStatus,
  );

  useEffect(() => {
    if (
      sendSmsStatus === "success" ||
      sendSmsReplaceInvalidStatus === "success"
    ) {
      triggerToast(t(`transactions.sendSms.success`) || "", "valid");
    } else if (
      sendSmsStatus === "failed" ||
      sendSmsReplaceInvalidStatus === "failed"
    ) {
      triggerToast(t("transactions.sendSms.failure") || "", "error");
    }
  }, [sendSmsReplaceInvalidStatus, sendSmsStatus, t]);

  useEffect(() => {
    if (!transaction) {
      navigate(`/${firstPath}/transactions/cards`);
    }
  }, [firstPath, navigate, transaction]);

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/transactions/cards`);
  }, [firstPath, navigate]);

  if (!transaction) {
    return;
  }

  return (
    <SidePanelDS.SidePanel visible={!!transaction} onClose={onSidePanelClosed}>
      <SidePanelDS.Section
        verticalSpacing={1.5}
        horizontalSpacing={1.5}
        background={colors["colors/button/secondary/hover"]}
      >
        <SidePanelHeader
          transaction={transaction}
          canCorrectMerchantCategory={props.canCorrectMerchantCategory ?? false}
          sendSmsReplaceInvalidAction={props.sendSmsReplaceInvalidAction}
          getSupportingDocumentAction={props.getSupportingDocumentAction}
          getLastReminderDateAction={props.getLastReminderDateAction}
          sendSmsAction={props.sendSmsAction}
        />
        <Spacer y={1} />
        <SidePanelSectionBlock
          transaction={transaction}
          canRedirectToRule={props.canRedirectToRule ?? false}
          firstPath={firstPath}
        />
      </SidePanelDS.Section>
      <LinkedTransactionBlock transaction={transaction} firstPath={firstPath} />
      <TransactionsDetails transaction={transaction} />
    </SidePanelDS.SidePanel>
  );
};
